type Nickname = string
type ComponentId = string

type GetNicknameKey = (options: {prefix: string; counter: number}) => string

interface GenerateNicknameOptions {
    prefix?: string
    getNicknameKey?: GetNicknameKey
}

const DEFAULT_PREFIX = 'comp'

class NicknameRegistry {
    private nicknameMap = new Map<Nickname, ComponentId>()
    private componentIdMap = new Map<ComponentId, Nickname>()

    public register(nickname: Nickname, componentId: ComponentId): void {
        const existingNickname = this.getNickname(componentId)
        if (existingNickname) {
            throw new Error(`Component already has nickname "${existingNickname}"`)
        }

        if (this.isNicknameTaken(nickname)) {
            throw new Error(`Nickname "${nickname}" is already taken`)
        }

        this.nicknameMap.set(nickname, componentId)
        this.componentIdMap.set(componentId, nickname)
    }

    public getComponentId(nickname: Nickname): ComponentId | undefined {
        return this.nicknameMap.get(nickname)
    }

    public getNickname(componentId: ComponentId): Nickname | undefined {
        return this.componentIdMap.get(componentId)
    }

    public isNicknameTaken(nickname: Nickname): boolean {
        return this.nicknameMap.has(nickname)
    }

    public getNicknameToIdMap(): Record<Nickname, ComponentId> {
        return Object.fromEntries(this.nicknameMap.entries())
    }

    public getIdToNicknameMap(): Record<ComponentId, Nickname> {
        return Object.fromEntries(this.componentIdMap.entries())
    }
}

const defaultGetNicknameKey: GetNicknameKey = ({prefix, counter}) => `${prefix}${counter}`

export class NicknamesGenerator {
    private prefixCounters = new Map<string, number>()
    private nicknameRegistry = new NicknameRegistry()
    private getNicknameKey: GetNicknameKey

    constructor(getNicknameKey: GetNicknameKey = defaultGetNicknameKey) {
        this.getNicknameKey = getNicknameKey
    }

    public generateNickname(componentId: ComponentId, options?: GenerateNicknameOptions): Nickname {
        const prefix = options?.prefix ?? DEFAULT_PREFIX
        const nickname = this.generateUniqueNickname(prefix, options?.getNicknameKey ?? this.getNicknameKey)
        this.setNickname(componentId, nickname)
        return nickname
    }

    public setNickname(componentId: ComponentId, nickname: Nickname): void {
        this.nicknameRegistry.register(nickname, componentId)
    }

    public getComponentId(nickname: Nickname): ComponentId | undefined {
        return this.nicknameRegistry.getComponentId(nickname)
    }

    public getNickname(componentId: ComponentId): Nickname | undefined {
        return this.nicknameRegistry.getNickname(componentId)
    }

    public getNicknameToIdMap(): Record<Nickname, ComponentId> {
        return this.nicknameRegistry.getNicknameToIdMap()
    }

    public getIdToNicknameMap(): Record<ComponentId, Nickname> {
        return this.nicknameRegistry.getIdToNicknameMap()
    }

    private generateUniqueNickname(prefix: string, getNicknameKey: GetNicknameKey = this.getNicknameKey): Nickname {
        const currentCount = this.prefixCounters.get(prefix) ?? 0

        let nickname: Nickname
        let nextCount: number = currentCount

        do {
            nickname = getNicknameKey({prefix, counter: ++nextCount})
        } while (this.nicknameRegistry.isNicknameTaken(nickname))

        this.prefixCounters.set(prefix, nextCount)

        return nickname
    }
}
